import {
  DropdownItem,
  AnsweredQuestionItem,
  Section,
  Question,
} from './formData.types';
import {
  DynamicFormAnswerInput,
  I18NContentInput,
} from './backendFormData.types';
import {
  SECTION_IDS,
  QUESTION_IDS,
  getDartAnswerValue,
  TRANSLATION_IDS,
} from './formData.constants';
import { FORM_DISPLAY_NAMES } from './formSectionConstants';

/**
 * Builds form answers from dropdown items
 */
export const buildDropdownAnswers = (
  dropdownItems: DropdownItem[],
  sections: Section[],
  formType: string
): DynamicFormAnswerInput[] => {
  const answers: DynamicFormAnswerInput[] = [];

  dropdownItems.forEach((item: DropdownItem) => {
    // Find the section containing this dropdown
    const section = sections.find(
      (s: Section) => s.SECTION_ID === SECTION_IDS.DART_DROPDOWNS
    );

    const question = section?.QUESTIONS.find(
      (q: Question) => q.QUESTION_ID === item.type?.toUpperCase()
    );

    if (!question) return;

    const questionText: I18NContentInput = {
      translationID: question.QUESTION_TEXT.TRANSLATION_ID,
      defaultMessage: question.QUESTION_TEXT.DEFAULT_MESSAGE,
    };

    // Handle special form type case
    const specialFormTypeCase = {
      defaultMessage: formType,
      translationID:
        formType === FORM_DISPLAY_NAMES.DART_ESCALATIONS_FORM
          ? TRANSLATION_IDS.DART_ESCALATIONS_TITLE
          : TRANSLATION_IDS.DART_CONSULTATION_TITLE,
    };

    const selectedOption = item.selected;
    let selectedOptionText = question.QUESTION_OPTIONS?.find(
      option => option.DEFAULT_MESSAGE === selectedOption
    );

    const selectedOptionInput: I18NContentInput | undefined = selectedOptionText
      ? {
          translationID: selectedOptionText.TRANSLATION_ID,
          defaultMessage: selectedOptionText.DEFAULT_MESSAGE,
        }
      : question.QUESTION_ID.includes(QUESTION_IDS.CONTACT_REVIEW_FORM_TYPE)
        ? specialFormTypeCase
        : undefined;

    const valueOfOption =
      selectedOptionText?.VALUE !== undefined &&
      selectedOptionText?.VALUE !== null
        ? { value: selectedOptionText.VALUE }
        : {};

    answers.push({
      questionID: question.QUESTION_ID,
      questionText,
      selectedOption: selectedOptionInput,
      ...valueOfOption,
    });
  });

  return answers;
};

/**
 * Builds form answers from answered questions
 */
export const buildQuestionAnswers = (
  questionsAnswered: Record<string, AnsweredQuestionItem>,
  sections: Section[],
  excludePlaceholders: string[]
): DynamicFormAnswerInput[] => {
  const answers: DynamicFormAnswerInput[] = [];

  Object.keys(questionsAnswered).forEach((key: string) => {
    // Skip if this is a dropdown placeholder
    if (excludePlaceholders.includes(key)) {
      return;
    }

    const questionData = questionsAnswered[key];

    // Skip if no answer provided
    if (
      questionData?.checkedValue === undefined &&
      questionData?.comments === undefined
    ) {
      return;
    }

    // Find the section and question
    const section = sections.find((s: Section) =>
      s.QUESTIONS.some((q: Question) => q.QUESTION_TEXT.DEFAULT_MESSAGE === key)
    );

    const question = section?.QUESTIONS.find(
      (q: Question) => q.QUESTION_TEXT.DEFAULT_MESSAGE === key
    );

    if (!question) return;

    const questionText: I18NContentInput = {
      translationID: question.QUESTION_TEXT.TRANSLATION_ID,
      defaultMessage: question.QUESTION_TEXT.DEFAULT_MESSAGE,
    };

    const answerText = questionData?.comments;

    const answerValue =
      questionData.checkedValue !== undefined
        ? getDartAnswerValue(questionData.checkedValue)
        : undefined;

    answers.push({
      questionID: question.QUESTION_ID,
      questionText,
      answerText,
      selectedOption: answerValue,
      value: questionData?.checkedValue,
    });
  });

  return answers;
};

/**
 * Builds manager summary answer
 */
export const buildManagerSummaryAnswer = (
  contactSummary: string,
  sections: Section[]
): DynamicFormAnswerInput | null => {
  const summarySection = sections.find(
    (section: Section) =>
      section.SECTION_ID === SECTION_IDS.DART_MANAGER_SUMMARY
  );

  if (!summarySection || !summarySection.QUESTIONS[0]) {
    return null;
  }

  const questionText: I18NContentInput = {
    translationID: summarySection.QUESTIONS[0].QUESTION_TEXT.TRANSLATION_ID,
    defaultMessage: summarySection.QUESTIONS[0].QUESTION_TEXT.DEFAULT_MESSAGE,
  };

  return {
    questionID: summarySection.QUESTIONS[0].QUESTION_ID,
    questionText,
    answerText: contactSummary,
  };
};

/**
 * Master function to build all form answers
 */
export const buildFormAnswers = ({
  dropdownItems,
  questionsAnswered,
  templateData,
  contactSummary,
  formType,
}: {
  dropdownItems: DropdownItem[];
  questionsAnswered: Record<string, AnsweredQuestionItem>;
  templateData: any;
  contactSummary?: string;
  formType: string;
}): DynamicFormAnswerInput[] => {
  const { SECTIONS } = templateData;
  const answers: DynamicFormAnswerInput[] = [];

  // Build dropdown answers
  const dropdownAnswers = buildDropdownAnswers(
    dropdownItems,
    SECTIONS,
    formType
  );
  answers.push(...dropdownAnswers);

  // Build question answers (exclude dropdown placeholders)
  const excludePlaceholders = dropdownItems.map(
    d => d.placeholder.DEFAULT_MESSAGE
  );
  const questionAnswers = buildQuestionAnswers(
    questionsAnswered,
    SECTIONS,
    excludePlaceholders
  );
  answers.push(...questionAnswers);

  // Build manager summary answer if provided
  if (contactSummary) {
    const summaryAnswer = buildManagerSummaryAnswer(contactSummary, SECTIONS);
    if (summaryAnswer) {
      answers.push(summaryAnswer);
    }
  }

  return answers;
};
